import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { merge, catchError, EMPTY, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-modal';
import { ContainerGradeRepairCodeService } from '../../../services/container-grade-repair-code.service';
import { ContainerGradeRepairCodeHeader } from '../../../models/container-grade-repair-code.model';
import { ContainerGradeRepairCodeFilterForm } from '../../../form-model/container-grade-repair-code-filter.form.model';
import { ContainerGradeRepairCodeFilter } from '../../../models/container-grade-repair-code-filter.model';
import { ContainerGradeRepairCodeSearchTextForm } from '../../../form-model/container-grade-repair-code-search-text.form.model';
import { ContainerGradeRepairCodeListGridColumns } from './container-grade-repair-code-grid.config';
import { AuthPolicies } from '../../../auth/auth-policies';
import { ToastrService } from '../../../services/toastr-service';
import { PaginationComponent } from '../../../shared-components/aems-pagination/aems-pagination.component';
import { parsePaginationHeaders, PaginationMeta } from '../../../helper/pagination-helpers';

@Component({
  selector: 'app-container-grade-repair-code-grid',
  imports: [CommonModule, RouterModule, NgxSkeletonLoaderModule, LoaderComponent, PaginationComponent],
  templateUrl: './container-grade-repair-code-grid.component.html',
  styleUrl: './container-grade-repair-code-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ContainerGradeRepairCodeGridComponent implements OnInit, AfterViewInit, OnDestroy, PaginationMeta {
  data: ContainerGradeRepairCodeHeader[] = [];
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 30 }, (_, i) => ({ id: i }));
  firstPage?: number;
  previousPage?: number;
  currentPage = 1;
  nextPage?: number;
  lastPage?: number;
  totalItemCount?: number;
  pageLimit = 20;
  sort: string | undefined;
  filterParam: ContainerGradeRepairCodeFilter = {};
  apiCallStatus: APICallStatus = 'NOT_STARTED';
  urlNavigate: string | undefined;
  openDeleteConfirmation = false;
  selectedRow: ContainerGradeRepairCodeHeader | null = null;

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    filterForm: FormGroup<ContainerGradeRepairCodeFilterForm>;
    searchTextForm: FormGroup<ContainerGradeRepairCodeSearchTextForm>;
  }>;
  columns = ContainerGradeRepairCodeListGridColumns;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private readonly loadTrigger$ = new Subject<boolean>();

  constructor(
    private readonly service: ContainerGradeRepairCodeService,
    private readonly router: Router,
    private readonly toastrService: ToastrService,
    public authPolicy: AuthPolicies,
  ) {}

  ngOnInit(): void {
    this.urlNavigate = '/master-data/container-grade-repair-code';
    this.loadTrigger$
      .pipe(
        switchMap(() =>
          this.service.getList(this.currentPage, this.pageLimit, this.sort, this.filterParam).pipe(
            catchError(() => {
              this.apiCallStatus = 'FAILED';
              this.data = [];
              return EMPTY;
            }),
          ),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response) => {
          this.data = response.body || [];
          if (response.headers) {
            Object.assign(this, parsePaginationHeaders(response.headers, this.currentPage));
          }
          this.apiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
      });
    this.getList();
  }

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private subscribeToFormChanges(): void {
    merge(
      this.filterFormGroup.controls.filterForm.get('applyFilter')!.valueChanges,
      this.filterFormGroup.controls.searchTextForm.get('appliedSearchTextFilter')!.valueChanges,
    )
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value === true) {
          this.onApplyFilter();
        }
      });
  }

  onSortDirectionChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const sortColumn: string = customEvent.detail.column;
    const sortDirection: string = customEvent.detail.direction === 'ascending' ? 'asc' : 'desc';
    if (sortColumn !== 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getList();
    }
  }

  onPageLimitChanged(newLimit: number): void {
    this.currentPage = 1;
    this.pageLimit = newLimit;
    this.getList();
  }

  onPageChanged(newPage: number): void {
    this.currentPage = newPage;
    this.getList(true);
  }

  onEdit(row: ContainerGradeRepairCodeHeader): void {
    const base = this.urlNavigate ?? '/master-data/container-grade-repair-code';
    this.router.navigate([base, row.repairCode, row.mode, 'edit']);
  }

  onDelete(row: ContainerGradeRepairCodeHeader): void {
    this.selectedRow = row;
    this.openDeleteConfirmation = true;
  }

  cancelDelete(): void {
    this.closeDeleteDialog();
  }

  confirmDelete(): void {
    if (!this.selectedRow) {
      this.closeDeleteDialog();
      return;
    }
    const row = this.selectedRow;
    this.service
      .delete(row.repairCode, row.mode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.closeDeleteDialog();
          this.getList();
          this.toastrService.showToastr(`Repair code ${row.repairCode} mode ${row.mode} mapping deleted successfully`, 'success');
        },
        error: () => {
          this.closeDeleteDialog();
          this.toastrService.showToastr('Failed to delete mapping', 'error');
        },
      });
  }

  private closeDeleteDialog(): void {
    this.openDeleteConfirmation = false;
    this.selectedRow = null;
  }

  onApplyFilter(): void {
    if (this.filterFormGroup.controls.filterForm.valid) {
      this.filterParam = this.extractFilterParams();
      this.currentPage = 1;
      this.getList();
    }
  }

  private extractFilterParams(): ContainerGradeRepairCodeFilter {
    const form = this.filterFormGroup.controls.filterForm;
    const filters: ContainerGradeRepairCodeFilter = {};

    for (const field of ['RepairCodeList', 'RepairModeList', 'GradesApplicableList'] as const) {
      const value = form.get(field)?.value as string[] | null;
      if (value?.length) {
        filters[field] = value;
      }
    }

    const generateGradeChange = form.get('GenerateGradeChange')?.value;
    if (generateGradeChange === 'yes') {
      filters.GenerateGradeChange = true;
    } else if (generateGradeChange === 'no') {
      filters.GenerateGradeChange = false;
    }

    const searchText = this.filterFormGroup.controls.searchTextForm.get('SearchText')?.value;
    if (searchText) {
      filters.SearchText = searchText;
    }

    return filters;
  }

  getList(isPageChange: boolean = false): void {
    this.apiCallStatus = 'IN_PROGRESS';
    this.loadTrigger$.next(isPageChange);
  }

  clearFilter(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.searchTextForm.get('appliedSearchTextFilter')?.setValue(true);
    this.filterFormGroup.controls.filterForm.get('applyFilter')?.setValue(true);
  }
}
